import Image from 'next/image';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { ComponentProps } from '@components/strapi/DynamicZoneComponent';
import { PostDetailSectionDTO } from '@lib/strapi/dtos/components/sections/postDetailSection.dto';
import { Prose } from '../shared-ui';
import { MDXRemoteSerializeResult } from 'next-mdx-remote';

/**
 * Blog article body — renders the post the /blog/[slug] route resolved into
 * page.entity. The article itself carries the content; this lays it out.
 */
const PostDetailSectionImplementation = ({ section, page }: ComponentProps<PostDetailSectionDTO>) => {
  const { asPath } = useRouter();
  const post = (page as {
    entity?: {
      title?: string; displayDate?: string; content?: MDXRemoteSerializeResult;
      featuredImage?: { url: string; alternativeText?: string };
      author?: { displayName?: string };
      categories?: { name?: string }[];
      teaser?: string; updatedAt?: string;
    };
  } | undefined)?.entity;
  if (!post) return null;

  // Article JSON-LD: headline + dates + image + the clinic as publisher.
  // The author joins only when the byline is shown (showAuthor) — markup must
  // not claim an attribution the page itself does not display.
  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    ...(post.teaser ? { description: post.teaser } : {}),
    ...(post.displayDate ? { datePublished: post.displayDate } : {}),
    ...(post.updatedAt ? { dateModified: post.updatedAt } : {}),
    ...(post.featuredImage?.url ? { image: [post.featuredImage.url] } : {}),
    ...(section?.showAuthor && post.author?.displayName
      ? { author: { '@type': 'Person', name: post.author.displayName } } : {}),
    publisher: { '@type': 'Organization', name: 'Physiowave Therapy', url: 'https://myphysiowave.gr' },
    mainEntityOfPage: `https://myphysiowave.gr${asPath.split(/[?#]/)[0]}`,
  };

  return (
    <article className="my-4">
      <Head>
        <script type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(articleLd) }} />
      </Head>
      <header className="measure">
        {post.categories?.length ? (
          <span className="rounded-full border border-violet-200 bg-violet-050 px-2.5 py-0.5 text-xs font-extrabold text-violet-700">
            {post.categories[0]?.name}
          </span>
        ) : null}
        <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-ink md:text-4xl">{post.title}</h1>
        <p className="mt-3 text-sm text-muted">
          {section?.showAuthor && post.author?.displayName ? `${post.author.displayName} · ` : ''}
          {section?.showPublishedDate && post.displayDate
            ? new Date(post.displayDate).toLocaleDateString('el-GR', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' })
            : ''}
        </p>
      </header>
      {post.featuredImage ? (
        <div className="relative mt-6 aspect-[16/8] overflow-hidden rounded-2xl bg-violet-050">
          <Image src={post.featuredImage.url} alt={post.featuredImage.alternativeText ?? post.title ?? ''} fill
            sizes="(min-width:1024px) 48rem, 100vw" className="clinical-photo" priority fetchPriority="high" />
        </div>
      ) : null}
      <Prose content={post.content} className="mt-8" />
    </article>
  );
};

export default PostDetailSectionImplementation;
